import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ShieldAlert,
    Users,
    Activity,
    Search,
    ArrowLeft,
    Loader2,
    Camera,
    Eye,
    Printer,
    EyeOff,
    Download,
    RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FocusHeader from "@/components/FocusHeader";
import OwnerAvatar from "@/components/OwnerAvatar";
import { TID } from "@/lib/testIds";

const ROOT_EMAIL = "dan@virtusmaritime.com";

const ACTION_META = {
    page_view: { label: "PAGE VIEW", icon: Eye, tone: "text-virtus-dim" },
    screenshot_attempt: { label: "SCREENSHOT", icon: Camera, tone: "text-virtus-stuck" },
    print_blocked: { label: "PRINT BLOCK", icon: Printer, tone: "text-yellow-400" },
    save_blocked: { label: "SAVE BLOCK", icon: Printer, tone: "text-yellow-400" },
    tab_hidden: { label: "TAB HIDDEN", icon: EyeOff, tone: "text-virtus-faint" },
    tab_visible: { label: "TAB VISIBLE", icon: Eye, tone: "text-virtus-faint" },
    window_blur: { label: "WINDOW BLUR", icon: EyeOff, tone: "text-virtus-faint" },
};

const SINCE_OPTIONS = [
    { key: "1h", label: "LAST 1H", hours: 1 },
    { key: "6h", label: "LAST 6H", hours: 6 },
    { key: "24h", label: "LAST 24H", hours: 24 },
    { key: "7d", label: "LAST 7D", hours: 24 * 7 },
    { key: "30d", label: "LAST 30D", hours: 24 * 30 },
];

export default function AdminAudit() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filterEmail, setFilterEmail] = useState("");
    const [filterAction, setFilterAction] = useState("");
    const [sinceKey, setSinceKey] = useState("24h");

    // Client-side gate so unauthorised users hit the redirect fast; backend
    // still enforces the same rule via _require_root.
    useEffect(() => {
        if (user && (user.email || "").toLowerCase() !== ROOT_EMAIL) {
            toast.error("Audit log is root-only.");
            nav("/hub");
        }
    }, [user, nav]);

    const load = async () => {
        setRefreshing(true);
        try {
            const hours = SINCE_OPTIONS.find((s) => s.key === sinceKey)?.hours || 24;
            const since = new Date(Date.now() - hours * 3600 * 1000).toISOString();
            const params = new URLSearchParams({ since, limit: "500" });
            if (filterEmail.trim()) params.append("email", filterEmail.trim().toLowerCase());
            if (filterAction.trim()) params.append("action", filterAction.trim());
            const [sumRes, logRes] = await Promise.all([
                api.get("/audit/log/summary"),
                api.get(`/audit/log?${params.toString()}`),
            ]);
            setSummary(sumRes.data);
            setEvents(logRes.data.events || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load audit log.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sinceKey]);

    const distinctActions = useMemo(() => {
        const set = new Set(events.map((e) => e.action));
        return Array.from(set).sort();
    }, [events]);

    const exportCsv = () => {
        const rows = [
            ["ts", "user_email", "action", "path", "ip", "user_agent"],
            ...events.map((e) => [
                e.ts,
                e.user_email || "",
                e.action || "",
                e.path || "",
                e.ip || "",
                (e.user_agent || "").replace(/,/g, " "),
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `virtus-audit-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden" data-testid={TID.adminAuditPage}>
            <FocusHeader
                code="AO/ROOT·AUDIT"
                title="Audit Log"
                subtitle="Every page view, screenshot attempt, and sensitive action, traced back to a user + session."
            />

            {/* Filters + summary strip */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <ShieldAlert size={12} className="text-virtus-orange" />
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        ROOT-ONLY · SIGNED IN AS DAN
                    </span>
                    <div className="flex items-center gap-1 ml-4">
                        {SINCE_OPTIONS.map((o) => (
                            <button
                                key={o.key}
                                onClick={() => setSinceKey(o.key)}
                                className={`font-mono text-[9px] tracking-[0.24em] uppercase px-2.5 py-1 border transition-colors ${
                                    sinceKey === o.key
                                        ? "border-virtus-orange text-virtus-orange bg-virtus-orange/10"
                                        : "border-virtus-line text-virtus-dim hover:border-virtus-orange hover:text-virtus-orange"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => nav("/admin/users")}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-3 py-1.5 hover:border-virtus-orange hover:text-virtus-orange"
                    >
                        <ArrowLeft size={11} /> USERS
                    </button>
                    <button
                        onClick={load}
                        disabled={refreshing}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-3 py-1.5 hover:border-virtus-orange hover:text-virtus-orange"
                    >
                        <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
                        REFRESH
                    </button>
                    <button
                        onClick={exportCsv}
                        disabled={events.length === 0}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black disabled:opacity-40"
                    >
                        <Download size={11} /> CSV
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-virtus-dim">
                    <Loader2 className="animate-spin" />
                </div>
            ) : (
                <div className="flex-1 grid grid-cols-[320px_1fr] gap-px bg-virtus-line overflow-hidden">
                    {/* Left rail: 24h summary */}
                    <div className="bg-virtus-bg overflow-y-auto p-4 space-y-5">
                        <SummarySection
                            icon={Users}
                            title="Top Users · 24h"
                            rows={(summary?.by_user || []).slice(0, 12).map((r) => ({
                                key: r.email,
                                left: r.email,
                                right: r.events,
                                sub: r.last_seen ? relativeTime(r.last_seen) : null,
                            }))}
                            empty="No activity in the last 24 hours."
                        />
                        <SummarySection
                            icon={Activity}
                            title="Actions · 24h"
                            rows={(summary?.by_action || []).map((r) => {
                                const meta = ACTION_META[r.action] || {};
                                return {
                                    key: r.action,
                                    left: (
                                        <span className={`flex items-center gap-1.5 ${meta.tone || "text-virtus-dim"}`}>
                                            {meta.icon ? <meta.icon size={11} /> : null}
                                            {meta.label || r.action.toUpperCase()}
                                        </span>
                                    ),
                                    right: r.count,
                                    sub: null,
                                };
                            })}
                            empty="No actions logged."
                        />
                    </div>

                    {/* Right pane: events table */}
                    <div className="bg-virtus-bg flex flex-col overflow-hidden">
                        <div className="px-4 py-2 border-b border-virtus-line flex items-center gap-2">
                            <div className="relative flex-1 max-w-[280px]">
                                <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-virtus-faint" />
                                <input
                                    value={filterEmail}
                                    onChange={(e) => setFilterEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && load()}
                                    placeholder="filter by email..."
                                    className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange pl-7 pr-2 py-1.5 text-white outline-none text-xs font-mono"
                                />
                            </div>
                            <select
                                value={filterAction}
                                onChange={(e) => {
                                    setFilterAction(e.target.value);
                                    setTimeout(load, 0);
                                }}
                                className="bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white outline-none text-xs font-mono"
                            >
                                <option value="">ALL ACTIONS</option>
                                {distinctActions.map((a) => (
                                    <option key={a} value={a} className="bg-black">
                                        {(ACTION_META[a]?.label || a).toUpperCase()}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={load}
                                className="font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-orange border border-virtus-orange px-2.5 py-1.5 hover:bg-virtus-orange hover:text-black"
                            >
                                APPLY
                            </button>
                            <span className="ml-auto font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                                {events.length} EVENTS
                            </span>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <div className="grid grid-cols-[140px_1fr_150px_1fr_120px] items-center gap-2 px-4 h-[26px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint sticky top-0 bg-virtus-bg z-10">
                                <div>WHEN</div>
                                <div>USER</div>
                                <div>ACTION</div>
                                <div>PATH</div>
                                <div>IP</div>
                            </div>
                            {events.length === 0 ? (
                                <div className="py-10 text-center font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint">
                                    &gt; NO EVENTS FOR THIS FILTER
                                </div>
                            ) : (
                                events.map((e) => {
                                    const meta = ACTION_META[e.action] || {};
                                    const Icon = meta.icon;
                                    return (
                                        <motion.div
                                            key={e.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="grid grid-cols-[140px_1fr_150px_1fr_120px] items-center gap-2 px-4 h-9 border-b border-virtus-line hover:bg-[#141414]"
                                            data-testid={TID.adminAuditRow(e.id)}
                                        >
                                            <div className="font-mono text-[11px] text-virtus-dim tabular-nums">
                                                {formatTs(e.ts)}
                                            </div>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <OwnerAvatar owner={e.user_email} size={18} />
                                                <span className="font-mono text-[11px] text-white truncate">
                                                    {e.user_email || "—"}
                                                </span>
                                            </div>
                                            <div className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase ${meta.tone || "text-virtus-dim"}`}>
                                                {Icon ? <Icon size={11} /> : null}
                                                {meta.label || e.action.toUpperCase()}
                                            </div>
                                            <div className="font-mono text-[11px] text-virtus-dim truncate" title={e.path}>
                                                {e.path || "—"}
                                            </div>
                                            <div className="font-mono text-[10px] text-virtus-faint tabular-nums truncate" title={e.ip}>
                                                {e.ip || "—"}
                                            </div>
                                        </motion.div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SummarySection({ icon: Icon, title, rows, empty }) {
    return (
        <div>
            <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.32em] uppercase text-virtus-orange mb-2">
                <Icon size={12} /> {title}
            </div>
            {rows.length === 0 ? (
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint">{empty}</div>
            ) : (
                <div className="border border-virtus-line">
                    {rows.map((r) => (
                        <div
                            key={r.key}
                            className="flex items-center justify-between px-3 h-8 border-b border-virtus-line last:border-b-0 hover:bg-[#141414]"
                        >
                            <div className="text-xs text-white truncate flex-1 min-w-0">
                                {r.left}
                                {r.sub && (
                                    <span className="ml-2 font-mono text-[9px] text-virtus-faint">
                                        {r.sub}
                                    </span>
                                )}
                            </div>
                            <div className="font-mono text-sm text-virtus-orange font-bold tabular-nums">
                                {r.right}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function formatTs(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const t = d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" });
        const day = d.toLocaleDateString("en-US", { day: "2-digit", month: "short", timeZone: "UTC" }).toUpperCase().replace(/,/g, "");
        return `${day} ${t}Z`;
    } catch (_) {
        return iso.slice(0, 19);
    }
}

function relativeTime(iso) {
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch (_) {
        return null;
    }
}
